// Живая валидация контакта в форме заявки. Чистый модуль (DOM нет) —
// тестируется в изоляции (contact.test.mjs), как blocks.ts и receipt.ts.
//
// ПРАВИЛА ТЕ ЖЕ, ЧТО НА СЕРВЕРЕ (AntiAbuse::PhoneNumber + Lead::EMAIL_RE):
// подсветка поля должна совпадать с приговором сервера, иначе посетитель
// отправляет форму и получает 422 — худший вариант из возможных. Сервер тут
// не «дублируется на всякий случай»: он остаётся единственным гарантом (форму
// обходят прямым POST), а клиент лишь избавляет от лишней отправки.
//
// Пустое поле для isValidPhone/isValidEmail валидно (они судят о ФОРМАТЕ);
// обязательность полей задаёт leadFieldErrorKey — она же знает, что имя и
// телефон в форме виджета обязательны, а почта нет.

/** Символы набора номера: цифры и разделители. Буквы — не номер («абвгд»),
 *  хвост «доб. 12» тоже отсекается: у сервера то же правило. */
const LEAD_PHONE_CHARS = /^[+()\d\s-]+$/;
const LEAD_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Телефон валиден: только символы набора и российский номер — зона +7, ровно
 *  11 цифр («+7 912…», «8 912…», «912…»). Пустой валиден (см. шапку модуля).
 *
 *  Зарубежные номера сейчас не принимаются (решение владельца 01.08.2026:
 *  «сейчас нужна только РФ»), поэтому «21341234123421» гаснет прямо в форме,
 *  не доходя до сервера.
 *
 *  ⚠️ Реестр кодов (AntiAbuse::PhonePlan: «+7 (234)…» — несуществующий код)
 *  живёт ТОЛЬКО на сервере: 189 кодов в бандле виджета — лишний вес и вторая
 *  копия, которая разойдётся с первой при обновлении реестра. Клиент судит о
 *  ФОРМЕ записи, сервер — о существовании кода; расхождение даёт 422, и форма
 *  показывает его текст («проверьте номер: например, +7 912 345-67-89»). */
export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if (!LEAD_PHONE_CHARS.test(trimmed)) return false;
  const digits = canonicalDigits(trimmed);
  return digits.length === 11 && digits.startsWith('7');
}

/** Цифры со страновым кодом — как сервер (national/international): националка
 *  без «+» получает семёрку («9…» из 10 цифр, «8…» из 11), ввод с «+»
 *  остаётся как есть — чужому номеру семёрку не приписываем, он просто не
 *  пройдёт проверку зоны. */
function canonicalDigits(trimmed: string): string {
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) return digits;
  if (digits.length === 10) return `7${digits}`;
  if (digits.length === 11 && digits.startsWith('8')) return `7${digits.slice(1)}`;
  return digits;
}

/** E-mail валиден: структура local@domain.tld; пустой — валиден. */
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === '' || LEAD_EMAIL_RE.test(trimmed);
}

/** Имя без цифр и лишних символов на лету (порт filterName marketpult): буквы,
 *  пробел, точка, дефис, апостроф. */
export function filterLeadName(value: string): string {
  return value.replace(/[^A-Za-zА-Яа-яЁё\s.'-]/g, '');
}

/** Имя годится: минимум две буквы. Отличить «Юборщшзщзгщ» от настоящей фамилии
 *  без словаря нельзя, но односимвольный и пустой ввод («.», «-», пробелы)
 *  менеджеру бесполезен и до заявки доходить не должен. */
export function isValidLeadName(value: string): boolean {
  return (filterLeadName(value).match(/[A-Za-zА-Яа-яЁё]/g) ?? []).length >= 2;
}

/** Поля формы заявки виджета. */
export type LeadFieldKind = 'name' | 'phone' | 'email';

/** Ключ ошибки поля или null, если поле в порядке.
 *
 *  Имя и ТЕЛЕФОН обязательны (решение владельца 03.08.2026): заявка без номера
 *  менеджеру бесполезна — перезванивать некуда, а почту в этой нише не читают.
 *  Почта — по желанию, но заполненная проверяется на формат.
 *
 *  Про формат заполненного поля говорим отдельной строкой от «укажите …»:
 *  посетитель с опечаткой в номере не должен читать «укажите телефон», глядя
 *  на заполненный телефон. */
export function leadFieldErrorKey(kind: LeadFieldKind, value: string): string | null {
  const trimmed = value.trim();
  if (kind === 'email') return isValidEmail(trimmed) ? null : 'lead_email_invalid';
  if (trimmed === '') return `lead_${kind}_required`;
  if (kind === 'name') return isValidLeadName(trimmed) ? null : 'lead_name_invalid';
  return isValidPhone(trimmed) ? null : 'lead_phone_invalid';
}

// ── Объединённое поле «телефон или e-mail» (форма лендинга) ────────────────
// Виджет спрашивает телефон и почту раздельно, лендинг — одной строкой. Правила
// при этом ОДНИ: и там и там приговор выносит сервер, и расходиться клиентам
// нельзя. Поэтому помощники живут здесь, рядом с базовыми проверками, а не
// второй копией в бандле лендинга.

/** Человек вводит почту, а не номер: буква или «собака» в наборе номера
 *  не встречаются. */
export function contactLooksLikeEmail(value: string): boolean {
  return /[A-Za-zА-Яа-яЁё@]/.test(value);
}

/** Российский номер по мере набора: «89123456789» → «+7 (912) 345-67-89».
 *
 *  Ввод без «+» дополняется семёркой (набрали «912…» или «8912…») — так человек
 *  диктует номер вслух. Ввод С «+» не трогаем: чужой зоне семёрку не
 *  приписываем, она просто не пройдёт проверку (принимаем пока только РФ). */
export function formatRuPhone(value: string): string {
  const plus = value.trimStart().startsWith('+');
  let digits = value.replace(/\D/g, '');
  if (digits === '') return plus ? '+' : '';
  if (!plus) {
    if (digits.startsWith('8')) digits = `7${digits.slice(1)}`;
    else if (digits.startsWith('9')) digits = `7${digits}`;
  }
  if (!digits.startsWith('7')) return `+${digits.slice(0, 15)}`;

  const rest = digits.slice(1, 11);
  let out = '+7';
  if (rest.length > 0) out += ` (${rest.slice(0, 3)}`;
  if (rest.length >= 3) out += ')';
  if (rest.length > 3) out += ` ${rest.slice(3, 6)}`;
  if (rest.length > 6) out += `-${rest.slice(6, 8)}`;
  if (rest.length > 8) out += `-${rest.slice(8, 10)}`;
  return out;
}

/** Снимает разметку номера, оставляя набранное. Нужно ровно в один момент:
 *  человек набрал «7788», увидел «+7 (778)» и дописал «@» — это была почта
 *  «7788@…», и скобки со пробелами надо убрать, иначе адрес испорчен. */
export function unformatPhone(value: string): string {
  return value.replace(/[\s()+-]/g, '');
}

/** Объединённое поле заполнено верно: либо почта, либо российский номер.
 *  Пустое — НЕ валидно (в отличие от раздельных полей виджета): на лендинге
 *  контакт обязателен, без него заявку не с кем связать. */
export function isValidContact(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return false;

  return contactLooksLikeEmail(trimmed) ? isValidEmail(trimmed) : isValidPhone(trimmed);
}
